import { Card, CardHeader } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import Email from "@/domain/entities/emails/Email";
import EventDetail from "@/domain/entities/emails/EventDetail";
import { cn } from "@/lib/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import EventCardMeetings, { EventCardVariant } from "./EventCardMeetings";

type MeetingsProps = {
    events: EventDetail[];
    mails?: Email[];
    showOnlyLatest?: boolean;
    compact?: boolean;
};

type EventWithModification = {
    event: EventDetail;
    originalEvent?: EventDetail;
    isModified: boolean;
    modifierName?: string;
};

const getEventTimestamp = (event: EventDetail): number =>
    new Date(event.eventSentDateTime).getTime();

const sortEventsByTimestamp = (events: EventDetail[]): EventDetail[] =>
    [...events].sort((a, b) => getEventTimestamp(a) - getEventTimestamp(b));

const createEventResult = (
    event: EventDetail,
    originalEvent?: EventDetail,
    modifierName?: string,
): EventWithModification => ({
    event,
    originalEvent,
    isModified: Boolean(originalEvent),
    modifierName,
});

const hasEventChanged = (
    original: EventDetail,
    modified: EventDetail,
): boolean => {
    const hasTimeChanged =
        original.startDateTime?.getTime() !==
            modified.startDateTime?.getTime() ||
        original.endDateTime?.getTime() !== modified.endDateTime?.getTime();

    const hasLocationChanged =
        original.location?.displayName !== modified.location?.displayName;

    return hasTimeChanged || hasLocationChanged;
};

const findMailByEvent = (
    event: EventDetail,
    mails: Email[],
): Email | undefined =>
    mails.find(
        (mail) =>
            mail.linkedEventDetails &&
            mail.linkedEventDetails.id === event.id &&
            getEventTimestamp(mail.linkedEventDetails) ===
                getEventTimestamp(event),
    );

const findModifierName = (
    sortedEvents: EventDetail[],
    mails?: Email[],
): string | undefined => {
    if (!mails || sortedEvents.length < 2) {
        return undefined;
    }

    let lastModifierName: string | undefined;

    for (let i = 1; i < sortedEvents.length; i++) {
        const previousEvent = sortedEvents[i - 1];
        const currentEvent = sortedEvents[i];

        if (hasEventChanged(previousEvent, currentEvent)) {
            const mailWithModifiedEvent = findMailByEvent(currentEvent, mails);

            if (mailWithModifiedEvent) {
                lastModifierName =
                    mailWithModifiedEvent.from.name ||
                    mailWithModifiedEvent.from.email;
            }
        }
    }

    return lastModifierName;
};

const processEventGroup = (
    eventsWithSameId: EventDetail[],
    mails?: Email[],
): EventWithModification => {
    if (eventsWithSameId.length === 1) {
        return createEventResult(eventsWithSameId[0]);
    }

    const sortedEvents = sortEventsByTimestamp(eventsWithSameId);
    const originalEvent = sortedEvents[0];
    const latestEvent = sortedEvents[sortedEvents.length - 1];

    if (hasEventChanged(originalEvent, latestEvent)) {
        const modifierName = findModifierName(sortedEvents, mails);

        return createEventResult(latestEvent, originalEvent, modifierName);
    }

    return createEventResult(latestEvent);
};

const groupEventsByIdMap = (
    events: EventDetail[],
): {
    eventsById: Map<string, EventDetail[]>;
    eventsWithoutId: EventDetail[];
} => {
    const eventsById = new Map<string, EventDetail[]>();
    const eventsWithoutId: EventDetail[] = [];

    events.forEach((event) => {
        if (!event.id) {
            eventsWithoutId.push(event);
        } else {
            const existing = eventsById.get(event.id) || [];
            eventsById.set(event.id, [...existing, event]);
        }
    });

    return { eventsById, eventsWithoutId };
};

const getLatestEventWithModifications = (
    events: EventDetail[],
    mails?: Email[],
): EventWithModification[] => {
    const sortedByLatest = [...events].sort(
        (a, b) => getEventTimestamp(b) - getEventTimestamp(a),
    );
    const latestEvent = sortedByLatest[0];

    if (!latestEvent.id) {
        return [createEventResult(latestEvent)];
    }

    const eventsWithSameId = events.filter(
        (event) => event.id === latestEvent.id,
    );

    return [processEventGroup(eventsWithSameId, mails)];
};

const getAllEventsWithModifications = (
    eventsById: Map<string, EventDetail[]>,
    eventsWithoutId: EventDetail[],
    mails?: Email[],
): EventWithModification[] => {
    const result: EventWithModification[] = [];

    eventsWithoutId.forEach((event) => {
        result.push(createEventResult(event));
    });

    eventsById.forEach((eventsWithSameId) => {
        result.push(processEventGroup(eventsWithSameId, mails));
    });

    return result;
};

const Meetings = ({
    events,
    mails,
    showOnlyLatest = false,
    compact = false,
}: MeetingsProps) => {
    const { t } = useTranslation();

    const eventsWithModifications = useMemo(() => {
        if (events.length === 0) {
            return [];
        }

        if (showOnlyLatest) {
            return getLatestEventWithModifications(events, mails);
        }

        const { eventsById, eventsWithoutId } = groupEventsByIdMap(events);

        return getAllEventsWithModifications(
            eventsById,
            eventsWithoutId,
            mails,
        );
    }, [events, mails, showOnlyLatest]);

    if (eventsWithModifications.length === 1) {
        return (
            <div className={cn("mb-2", compact ? "" : "mx-5")}>
                <EventCardMeetings
                    key={eventsWithModifications[0].event.id}
                    compact={compact}
                    event={eventsWithModifications[0].event}
                    modifierName={eventsWithModifications[0].modifierName}
                    originalEvent={eventsWithModifications[0].originalEvent}
                    variant={EventCardVariant.SINGLE}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-1 flex-col gap-1">
            <h2
                className={cn(
                    "text-text-main-title text-left text-lg font-bold",
                )}
            >
                {t("widgets.meetings.title", {
                    count: events.length,
                })}
            </h2>
            {events.length === 0 ? (
                <Card variant="event">
                    <CardHeader className="mb-0 px-4 text-sm">
                        {t("widgets.meetings.noMeetings")}
                    </CardHeader>
                </Card>
            ) : (
                <ScrollArea variant="ghost" viewportClassName="max-h-45">
                    <div className="flex flex-col gap-2">
                        {eventsWithModifications.map((eventWithMod, index) => (
                            <EventCardMeetings
                                key={eventWithMod.event.id || `event-${index}`}
                                compact={compact}
                                event={eventWithMod.event}
                                modifierName={eventWithMod.modifierName}
                                originalEvent={eventWithMod.originalEvent}
                            />
                        ))}
                    </div>
                </ScrollArea>
            )}
        </div>
    );
};

export default Meetings;
